import { FastTrackPanel } from "@/components/molecules/fast-track";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateFaq } from "@/features/faq/hooks/use-faq";
import { useTranslation } from "@/features/utils/i18n";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

const CATEGORY_LIST_ID = "faq-create-category-suggestions";

export const CreateFaqDialog = ({
  categories = [],
}: {
  categories?: string[];
}) => {
  const { t } = useTranslation("faq");
  const createFaq = useCreateFaq();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [category, setCategory] = useState("");
  const [refinedResponse, setRefinedResponse] = useState("");

  const reset = () => {
    setQuestion("");
    setCategory("");
    setRefinedResponse("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    try {
      await createFaq.mutateAsync({
        question,
        category,
        refined_response: refinedResponse,
      });
      toast.success(t("messages.createSuccess"));
      setOpen(false);
      reset();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.createError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" weight="bold" />
          {t("addFaq")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleCreate}>
          <DialogHeader>
            <DialogTitle>{t("create.title")}</DialogTitle>
            <DialogDescription>{t("create.description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <FastTrackPanel scope="faq" onLeave={() => setOpen(false)} />

            <div className="space-y-2">
              <Label htmlFor="new-faq-question">{t("refine.question")}</Label>
              <Input
                id="new-faq-question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-faq-category">{t("refine.category")}</Label>
              <Input
                id="new-faq-category"
                list={CATEGORY_LIST_ID}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={t("refine.categoryPlaceholder")}
              />
              <datalist id={CATEGORY_LIST_ID}>
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-faq-response">
                {t("refine.refinedResponse")}
              </Label>
              <Textarea
                id="new-faq-response"
                value={refinedResponse}
                onChange={(e) => setRefinedResponse(e.target.value)}
                rows={6}
                placeholder={t("refine.refinedPlaceholder")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("refine.cancel")}
            </Button>
            <Button type="submit" disabled={createFaq.isPending}>
              {t("create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
